/* eslint-disable no-console */
import { getDatadogLogger } from '@storebrand-digital/common-datadog/dist/logging';

import { isDevEnv } from '../app/utils/env';

import { type BrowserLogger, type Exception, type MonitoringLogger } from './types';
import { getExceptionMessage } from './utils';

function log<T extends Exception>(exception: T, monitoringLogger: MonitoringLogger, browserLogger: BrowserLogger) {
  const message = getExceptionMessage(exception);

  try {
    const environment = window.env.ENVIRONMENT;

    if (isDevEnv) {
      console.info(`Monitor logging disabled for "${environment}" environment`);
      return;
    }

    const errorObject = exception instanceof Error ? exception : undefined;
    monitoringLogger(message, {}, errorObject);
  } catch (e) {
    console.error(`Cannot send exception to Datadog: ${getExceptionMessage(e as Exception)}`);
  } finally {
    browserLogger(message);
  }
}

function errorLogger(message: string, messageContext?: object, error?: Error) {
  getDatadogLogger().error(message, messageContext, error);
}

/**
 * Use console.error in the browser and send exceptions to the monitoring platform
 *
 * @param exception Expects an `Error` object.
 * It's also possible to pass non-`Error` objects and strings,
 * but be aware that the resulting events in Sentry may be missing a stack trace.
 */
export function logError<T extends Exception>(exception: T) {
  log(exception, errorLogger, console.error);
}

function warnLogger(message: string, messageContext?: object, error?: Error) {
  getDatadogLogger().warn(message, messageContext, error);
}

/**
 * Use console.warn in the browser and send exceptions to the monitoring platform
 *
 * @param exception Expects an `Error` object.
 * It's also possible to pass non-`Error` objects and strings,
 * but be aware that the resulting events in Sentry may be missing a stack trace.
 */
export const logWarn = <T extends Exception>(exception: T) => {
  log(exception, warnLogger, console.warn);
};
